import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  clientSearch: null,
  clientForm: null,
  clientFormClientPhone: null,
  clientFormCoverageAreas: [],
  clientFormCoverageAreaSearch: null,
  clientFormCoverageAreaAddress: null,
  clients: {
    items: [],
    page: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  },
  clientFormPayload: null,
  clientCreateActiveStep: 0,
  focusFormCoverageArea: null,
};

export const clientStateSlice = createSlice({
  name: "clientState",
  initialState,
  reducers: {
    setClientSearch: (state, action) => {
      // Assuming action.payload is the search term
      if (!action.payload) {
        state.clientSearch = null;
        return;
      }
      state.clientSearch = state.clients.items.filter((client) => {
        const name = client.clientCompanyName || `${client.firstName} ${client.lastName}`;
        return name.toLowerCase().includes(action.payload.toLowerCase());
      });
    },
    updateClienForm: (state, action) => {
      state.clientForm = action.payload;
    },
    resetClientForm: (state) => {
      state.clientForm = null;
      state.clientFormClientPhone = null;
    },
    updateClientFormPhoneCountryCode: (state, action) => {
      state.clientFormClientPhone = action.payload;
    },
    updateClients: (state, action) => {
      // Legacy support if needed, but we should prefer setClientsPaginated
      if (Array.isArray(action.payload)) {
        state.clients.items = action.payload;
      } else {
        state.clients = action.payload;
      }
    },

    setClientsPaginated: (state, action) => {
      state.clients = action.payload;
    },

    addClient: (state, action) => {
      state.clients.items = [action.payload, ...state.clients.items];
      state.clients.totalItems += 1;
    },

    updateClientInList: (state, action) => {
      const updatedClient = action.payload;
      const index = state.clients.items.findIndex((client) => client.uid === updatedClient.uid);

      if (index !== -1) {
        state.clients.items[index] = { ...state.clients.items[index], ...updatedClient };
      } else {
        state.clients.items = [updatedClient, ...state.clients.items];
        state.clients.totalItems += 1;
      }
    },

    deleteClients: (state, action) => {
      const uidToDelete = typeof action.payload === "string" ? action.payload : action.payload?.uid;
      state.clients.items = state.clients.items.filter((client) => client?.uid !== uidToDelete);
      state.clients.totalItems = Math.max(0, state.clients.totalItems - 1);
    },

    nextClientCreateActiveStep: (state) => {
      state.clientCreateActiveStep = state.clientCreateActiveStep + 1;
    },

    backClientCreateActiveStep: (state) => {
      state.clientCreateActiveStep = state.clientCreateActiveStep > 0 ? state.clientCreateActiveStep - 1 : 0;
    },
    resetClientCreateActiveStep: (state) => {
      state.clientCreateActiveStep = 0;
    },

    resetClientFormCoverageAreas: (state) => {
      state.clientFormCoverageAreas = [];
      state.clientFormCoverageAreaSearch = null;
    },

    updateClientFormCoverageAreas: (state, action) => {
      state.clientFormCoverageAreas = action.payload;
      state.focusFormCoverageArea = action.payload.length > 0 ? action.payload[action.payload.length - 1] : null;
    },

    updateFocusFormCoverageArea: (state, action) => {
      state.focusFormCoverageArea = action.payload;
    },

    createClientFormCoverageAreas: (state, action) => {
      state.clientFormCoverageAreas = [...state.clientFormCoverageAreas, action.payload];
    },
    deleteClientFormCoverageAreas: (state, action) => {
      state.clientFormCoverageAreas = state.clientFormCoverageAreas.filter(
        (clientFormCoverageArea) =>
          !(clientFormCoverageArea?.latitude === action.payload?.latitude && clientFormCoverageArea?.longitude === action.payload?.longitude),
      );
      state.focusFormCoverageArea =
        state.clientFormCoverageAreas.length > 0 ? state.clientFormCoverageAreas[state.clientFormCoverageAreas.length - 1] : null;
    },

    resetClientFormCoverageAreaAddress: (state) => {
      state.clientFormCoverageAreaAddress = null;
    },

    updateClientFormCoverageAreaAddress: (state, action) => {
      state.clientFormCoverageAreaAddress = action.payload;
    },
    updateClientFormPayload: (state, action) => {
      const { phone, phones, emails, email, ...clientForm_ } = state.clientForm || {};

      const mappedEmails = Array.isArray(emails)
        ? emails
            .map((e) => {
              if (typeof e === "string") {
                return {
                  value: e,
                  tag: "other",
                  isDefault: false,
                };
              }
              return {
                value: e?.value || e?.email || "",
                tag: e?.tag || "other",
                isDefault: e?.isDefault || undefined,
              };
            })
            .filter((e) => e.value)
        : [];

      const mappedPhoneNumbers = Array.isArray(phones)
        ? phones
            .map((p) => {
              const cleanPhone = typeof p.phone === "string" ? p.phone.replace(/\D/g, "") : "";
              const dialCode = p.dialCode || "+1";
              return {
                countryCode: p.countryCode || "US",
                dialCode: dialCode,
                phone: cleanPhone,
                phoneNumber: dialCode + cleanPhone,
                tag: p.tag || undefined,
                isDefault: p.isDefault || undefined,
              };
            })
            .filter((p) => p.phone)
        : [];

      state.clientFormPayload = {
        ...clientForm_,
        emails: mappedEmails,
        phoneNumbers: mappedPhoneNumbers,
        coverageAreas: state.clientFormCoverageAreas,
      };
    },

    clearClientFormPayload: (state) => {
      state.clientFormPayload = null;
    },

    resetClientFormPayload: (state, action) => {
      state.clientCreateActiveStep = 0;
      state.clientFormPayload = null;
      state.clientForm = null;
      state.clientFormClientPhone = null;
    },

    updateClientFormCoverageAreaSearch: (state, action) => {
      state.clientFormCoverageAreaSearch = action.payload;
    },
    resetClientFormCoverageAreaSearch: (state, action) => {
      state.clientFormCoverageAreaSearch = null;
    },
  },
});

export const {
  setClientSearch,
  updateClienForm,
  updateClientFormPhoneCountryCode,
  resetClientForm,
  addClient,
  updateClients,
  setClientsPaginated,
  updateClientInList,
  deleteClients,
  updateClientCreateActiveStep,
  resetClientCreateActiveStep,
  nextClientCreateActiveStep,
  backClientCreateActiveStep,
  resetClientFormCoverageAreas,
  createClientFormCoverageAreas,
  deleteClientFormCoverageAreas,
  resetClientFormCoverageAreaAddress,
  updateClientFormCoverageAreaSearch,
  updateClientFormCoverageAreas,
  updateFocusFormCoverageArea,
  updateClientFormPayload,
  resetClientFormPayload,
  clearClientFormPayload,
} = clientStateSlice.actions;

export default clientStateSlice.reducer;
